// ProfileUI.js — Primitivos visuais compartilhados pelas seções do perfil.
// Centraliza os tokens de estilo (cards, inputs, botões, toast, badge) para
// manter consistência entre Dados Pessoais, Endereços, Pagamentos, etc.
//
// O campo de formulário NÃO mora mais aqui: ele foi para `ui/Formulario.js`,
// que é o mesmo arquivo que o login e o checkout importam. Antes eram quatro
// cópias do mesmo <input>, com medidas divergentes. `Field`, `TextInput` e
// `TextArea` continuam sendo exportados daqui para não quebrar os nove
// arquivos do perfil que já os importam — agora são só apelidos.
import React from "react";
import { Campo, TextInput as CampoTexto, TextArea as CampoArea } from "../../ui/Formulario";
import Reveal from "../../ui/Reveal";
import { message } from "../../utils/toast";

// Contêiner de seção do perfil — é a classe `.bloco` do App.css, e não uma
// receita escrita à mão.
//
// `.bloco` é o papel certo: uma faixa de largura inteira que agrupa um assunto.
// Não é `.cartao` de propósito — a sombra dura de 4px existe para o objeto que
// se repete (a oferta na prateleira, a linha do pedido), e numa faixa de 900px
// ela parece torta em vez de elevada. Quem empilha SectionCards não deve ver
// uma pilha de objetos soltos, e sim um formulário dividido em assuntos.
//
// A aparição de entrada mora AQUI, e não em cada aba: este é o único ponto por
// onde Minha Lista, Avaliações, Insigne Coins e Dados passam. O `as="section"` do
// Reveal existe justamente para casos assim — um <div> a mais em volta quebraria
// as grades e o `space-y` de quem empilha SectionCards.
//
// CUIDADO ao pôr conteúdo aqui dentro: enquanto a entrada roda (~1,2 s) a seção
// tem `transform`, e transform vira bloco de contenção. Um overlay
// `position: fixed` declarado DENTRO de um SectionCard passa a se medir pela
// seção nesse intervalo, em vez de pela janela. Os três modais do perfil
// (Endereços, Senha e Segurança, Avaliações) foram movidos para FORA do cartão
// por causa disso — modal não é conteúdo de cartão, ele cobre a tela inteira.
// Quem for acrescentar um novo: irmão do SectionCard, não filho.
export const SectionCard = ({ title, action, children, className = "" }) => (
  <Reveal as="section" className={"bloco p-6 md:p-8 " + className}>
    {(title || action) && (
      <div className="flex items-center justify-between gap-4 mb-6">
        {title && <h2 className="titulo-display text-[2.1rem] md:text-[2.5rem] text-white">{title}</h2>}
        {action}
      </div>
    )}
    {children}
  </Reveal>
);

// Linha divisória sutil (usada abaixo de títulos / entre itens)
export const Divider = ({ className = "" }) => (
  <div className={"h-px bg-white/10 " + className} />
);

// Campo, entrada e área de texto: apelidos do primitivo compartilhado.
export const Field = Campo;
export const TextInput = CampoTexto;
export const TextArea = CampoArea;

// Botões
//
// A AÇÃO PRINCIPAL É `.btn-solido` (App.css), a mesma peça do "Finalizar
// compra" e do "Voltar para a loja" da 404. Antes esta era uma sétima cópia do
// botão laranja do site, escrita com `bg-insigne-orange hover:bg-...-dark`: o
// perfil inteiro tinha um botão que escurecia no hover enquanto o resto do site
// tinha um que INVERTE (o fundo vira papel, a cor migra para o texto e para a
// borda tracejada). A classe também resolve o foco de teclado sozinha — o
// `:focus-visible` dispara a mesma inversão —, e por isso não há `foco-laranja`
// aqui: contorno laranja em volta de um botão laranja não se vê.
//
// O que continua sendo Tailwind é só o que a classe não cobre: forma, respiro,
// corpo do texto e o estado desabilitado.
//
// O `px-5` FICA, mesmo sendo uma das medidas que o Bootstrap sequestra (ali ele
// vale 3rem, não 1,25rem). Não é descuido: TODO botão do site é escrito assim —
// o do Clube, o do catálogo, o da 404 —, então o valor "errado" é o que dá a
// largura comum a todos eles. Corrigir só aqui deixaria os botões do perfil
// visivelmente mais estreitos que os do resto do site.
export const PrimaryButton = ({ className = "", children, ...props }) => (
  <button
    className={
      "btn-solido inline-flex items-center justify-center gap-2 rounded-controle px-5 py-2.5 text-sm font-semibold " +
      "disabled:opacity-60 disabled:cursor-not-allowed " +
      className
    }
    {...props}
  >
    {children}
  </button>
);

// O vermelho daqui é literal, e continua assim de propósito: o token vermelho
// da paleta é `insigne-ticket`, que identifica a MOEDA Ticket em todo o site.
// Pintar "Remover Conta" com ele faria o botão mais destrutivo da tela usar a
// cor de um crédito. Enquanto não existir um token de perigo, o hex fica.
export const DangerButton = ({ className = "", children, ...props }) => (
  <button
    className={
      "inline-flex items-center justify-center gap-2 rounded-controle px-5 py-2.5 text-sm font-semibold " +
      "bg-[#c0392b] text-paper hover:bg-[#a93226] transition-colors disabled:opacity-60 disabled:cursor-not-allowed " +
      className
    }
    {...props}
  >
    {children}
  </button>
);

export const GhostButton = ({ className = "", children, ...props }) => (
  <button
    className={
      "foco-laranja inline-flex items-center justify-center gap-2 rounded-controle px-4 py-2 text-sm font-medium " +
      "border border-line bg-white/5 text-white/90 hover:bg-white/10 transition-colors " +
      className
    }
    {...props}
  >
    {children}
  </button>
);

// Pílula de status (ex.: "Padrão" verde, "Crédito", etc.)
export const Badge = ({ children, color = "green", className = "" }) => {
  const colors = {
    green: "bg-emerald-400 text-emerald-950",
    blue: "bg-insigne-navy-light text-paper",
    gray: "bg-white/10 text-white/80",
  };
  return (
    <span className={"inline-flex items-center px-3 py-1 rounded-selo text-xs font-bold " + (colors[color] || colors.gray) + " " + className}>
      {children}
    </span>
  );
};

// Toast — hoje é só um encaixe vazio.
//
// O site tinha TRÊS sistemas de aviso, em três posições e três paletas: este
// (topo 150px, verde/vermelho chapados), o de `MeusLivros.js` (canto direito) e
// o global de `utils/toast.js` (topo centro, tokens de tema, `aria-live`).
// Só o global anunciava para leitor de tela. Os avisos do perfil agora passam
// por ele — quem dispara é o `useToast` abaixo.
//
// O componente continua existindo e continua sendo montado pelas seções do
// perfil porque remover as sete tags <Toast /> não mudaria nada na tela e só
// aumentaria a superfície desta alteração. Ele não desenha nada.
export const Toast = () => null;

// Cabeçalho de subtela com botão "voltar" (ex.: "Adicionar Endereço", "Editando: ...")
export const BackHeader = ({ title, onBack }) => (
  <div className="flex items-center gap-4 mb-6">
    <button
      onClick={onBack}
      aria-label="Voltar"
      className="foco-laranja w-10 h-10 rounded-controle border border-line bg-white/5 text-white
                 flex items-center justify-center hover:bg-white/10 transition-colors"
    >
      <i className="bi bi-arrow-left text-lg" />
    </button>
    <h2 className="titulo-display text-[2.1rem] md:text-[2.5rem] text-white">{title}</h2>
  </div>
);

// Hook de toast: mantém a assinatura { toast, fireToast } que as seções do
// perfil já usam, mas encaminha para o Toaster global. O objeto `toast` que ele
// devolve existe só para as chamadas `<Toast {...toast} />` continuarem válidas.
export const useToast = () => {
  const fireToast = (texto, type = "success") => {
    (message[type] || message.info)(texto);
  };
  return { toast: {}, fireToast };
};
